import React, {useEffect, useMemo, useRef, useState} from "react";
import {Card} from "primereact/card";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {FileUpload} from "primereact/fileupload";
import {Button} from "primereact/button";
import {InputTextarea} from "primereact/inputtextarea";
import "react-quill/dist/quill.snow.css";
import {useParams, useSearchParams} from "react-router-dom";
import {webInfo} from "../../../utils/webInfo";
import useApi from "../../../hooks/useApi";
import {Badge} from "primereact/badge";
import callToast, {catchError, showErrors} from "../../../utils/helper";
import {Skeleton} from "primereact/skeleton";
import usePost from "../../../hooks/usePost";
import useDelete from "../../../hooks/useDelete";
import {Calendar} from "primereact/calendar";
import moment from "moment";
import {Toast} from "primereact/toast";
import TextEditor from "../../../components/TextEditor";
import useStore from "../../../hooks/useStore";

const CareerDetails = () => {
    const toast = useRef<any>(null);
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const {user} = useStore()

    const url = searchParams.get("url");
    const division = searchParams.get("division");

    const { get, loading } = useApi<any>();
    const { post, loading: postLoading } = usePost();
    const { remove } = useDelete();

    const link = `${webInfo.baseUrl}/api/admin/careers/${id}`;
    const url2 = `${webInfo.baseUrl}/api/admin/companies`;

    const [depots, setDepots] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);

    const [description, setDescription] = useState("");
    const [jobRequirement, setJobRequirement] = useState("");
    const [educationRequirement, setEducationRequirement] = useState("");
    const [jobBenefits, setJobBenefits] = useState("");
    const [howToApply, setHowToApply] = useState("");

    const [career, setCareer] = useState<any>({
        title: "",
        short_description: "",
        address: "",
        image: "",
        company_id: 0,
        company_department_id: 0,
        documents: [{ id: 1, name: "", file: null, disabled: false }],
        number_of_vacancy: "",
        salary_from: "",
        salary_to: "",
        job_type: "",
        meta_title: "",
        tags: "",
        meta_keywords: "",
        meta_description: "",
        meta_robots: "",
        meta_author: "",
        meta_image: "",
        expired_date: null,
    });

    const companyOptions = useMemo(() => {
        return depots?.map((item: any) => ({
            name: item?.name,
            code: item?.id,
        }));
    }, [depots]);

    const selectedCompany = useMemo(() => {
        return companyOptions?.find(
            (item: any) => Number(item?.code) === Number(career.company_id)
        );
    }, [companyOptions, career.company_id]);

    const selectedDepartment = useMemo(() => {
        return departments?.find(
            (item: any) => Number(item?.code) === Number(career.company_department_id)
        );
    }, [departments, career.company_department_id]);

    const getPreviewUrl = (fileOrPath: any) => {
        if (!fileOrPath) return "";

        if (fileOrPath instanceof File) {
            return URL.createObjectURL(fileOrPath);
        }

        return `${webInfo.baseUrl}/${fileOrPath}`;
    };

    const getFileName = (fileOrPath: any) => {
        if (!fileOrPath) return "";

        if (fileOrPath instanceof File) {
            return fileOrPath.name;
        }

        return String(fileOrPath).split("/").at(-1) || "";
    };

    useEffect(() => {
        const fetchCareerDetails = async () => {
            try {
                const res: any = await get(link);
                const data = res?.data || {};

                const {
                    description,
                    job_requirement,
                    education_requirement,
                    how_to_apply,
                    job_benefits,
                    title,
                    short_description,
                    address,
                    image,
                    company_id,
                    company_department_id,
                    documents = [],
                    number_of_vacancy,
                    salary_from,
                    salary_to,
                    job_type,
                    meta_title,
                    tags,
                    meta_keywords,
                    meta_description,
                    meta_robots,
                    meta_author,
                    meta_image,
                    expired_date,
                } = data;

                setDescription(description || "");
                setJobRequirement(job_requirement || "");
                setEducationRequirement(education_requirement || "");
                setJobBenefits(job_benefits || "");
                setHowToApply(how_to_apply || "");

                const newDocuments =
                    documents?.length > 0
                        ? documents.map((item: any) => ({
                            ...item,
                            disabled: true,
                        }))
                        : [{ id: 1, name: "", file: null, disabled: false }];

                let depotData: any[] = [];
                let departmentOptions: any[] = [];

                try {
                    const res2: any = await get(url2);
                    depotData = res2?.data?.data || [];

                    setDepots(depotData);

                    const foundCompany = depotData.find(
                        (item: any) => Number(item?.id) === Number(company_id)
                    );

                    if (foundCompany?.departments?.length) {
                        departmentOptions = foundCompany.departments.map((item: any) => ({
                            name: item?.name,
                            code: item?.id,
                        }));
                    }

                    setDepartments(departmentOptions);
                } catch (err) {
                    console.error("Failed to fetch companies", err);
                    setDepots([]);
                    setDepartments([]);
                }

                setCareer({
                    title: title || "",
                    short_description: short_description || "",
                    address: address || "",
                    image: image || "",
                    company_id: company_id || 0,
                    company_department_id: company_department_id || 0,
                    documents: newDocuments,
                    number_of_vacancy: number_of_vacancy || "",
                    salary_from: salary_from || "",
                    salary_to: salary_to || "",
                    job_type: job_type || "",
                    meta_title: meta_title || "",
                    tags: tags || "",
                    meta_keywords: meta_keywords || "",
                    meta_description: meta_description || "",
                    meta_robots: meta_robots || "",
                    meta_author: meta_author || "",
                    meta_image: meta_image || "",
                    expired_date: expired_date
                        ? moment(expired_date, "YYYY-MM-DD HH:mm:ss").toDate()
                        : null,
                });
            } catch (err) {
                console.error("Failed to fetch career", err);
            }
        };

        fetchCareerDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, link, url2, url, division]);

    const handleInput = (e: any) => {
        const { name, value } = e.target;

        setCareer((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setCareer((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const onUpload = (event: any) => {
        setCareer((prev: any) => ({
            ...prev,
            image: event.files[0],
        }));
    };

    const onRemove = () => {
        setCareer((prev: any) => ({
            ...prev,
            image: "",
        }));
    };

    const onMetaUpload = (event: any) => {
        setCareer((prev: any) => ({
            ...prev,
            meta_image: event.files[0],
        }));
    };

    const onMetaRemove = () => {
        setCareer((prev: any) => ({
            ...prev,
            meta_image: "",
        }));
    };

    const conditionalDepartment: any =
        Number(user?.role) === 2
            ? departments?.filter(
                (item: any) =>
                    item?.name === "Sales" || item?.name === "Marketing"
            )
            : departments;

    const defaultImagePreviewer = () => {
        return (
            <div className="flex align-items-center cursor-pointer">
                {career.image ? (
                    <div className="p-fileupload-preview flex justify-content-between items-center w-full">
                        <div className="flex">
                            <img alt="Default" src={getPreviewUrl(career.image)} height="50" />
                            <div className="ml-2">
                                <p className="mb-2">{getFileName(career.image)}</p>
                                <div className="flex align-items-center">
                                    <p className="m-0">
                                        {career.image instanceof File
                                            ? `${(career.image.size / 1024).toFixed(2)} KB`
                                            : "00.00 KB"}
                                    </p>
                                    <Badge className="ml-2" value="Default" />
                                </div>
                            </div>
                        </div>

                        <Button
                            onClick={() =>
                                setCareer((prev: any) => ({
                                    ...prev,
                                    image: "",
                                }))
                            }
                            icon="pi pi-times"
                            className="mr-2"
                            rounded
                            text
                            severity="danger"
                            aria-label="Cancel"
                        />
                    </div>
                ) : (
                    <span
                        style={{
                            fontSize: "1em",
                            color: "var(--text-color-secondary)",
                        }}
                    >
            Upload Image
          </span>
                )}
            </div>
        );
    };

    const defaultMetaImagePreviewer = () => {
        return (
            <div className="flex align-items-center cursor-pointer">
                {career.meta_image ? (
                    <div className="p-fileupload-preview flex justify-content-between items-center w-full">
                        <div className="flex">
                            <img
                                alt="Default"
                                src={getPreviewUrl(career.meta_image)}
                                height="50"
                            />
                            <div className="ml-2">
                                <p className="mb-2">{getFileName(career.meta_image)}</p>
                                <div className="flex align-items-center">
                                    <p className="m-0">
                                        {career.meta_image instanceof File
                                            ? `${(career.meta_image.size / 1024).toFixed(2)} KB`
                                            : "00.00 KB"}
                                    </p>
                                    <Badge className="ml-2" value="Default" />
                                </div>
                            </div>
                        </div>

                        <Button
                            onClick={() =>
                                setCareer((prev: any) => ({
                                    ...prev,
                                    meta_image: "",
                                }))
                            }
                            icon="pi pi-times"
                            className="mr-2"
                            rounded
                            text
                            severity="danger"
                            aria-label="Cancel"
                        />
                    </div>
                ) : (
                    <span
                        style={{
                            fontSize: "1em",
                            color: "var(--text-color-secondary)",
                        }}
                    >
            Upload Image
          </span>
                )}
            </div>
        );
    };

    const defaultPdfPreviewer = (item: any) => {
        return (
            <div className="flex align-items-center cursor-pointer">
                {item.file ? (
                    <div className="p-fileupload-preview flex justify-content-between items-center w-full">
                        <div className="flex">
                            <img alt="Default" src="/pdf.jpg" height={50} />
                            <div className="ml-2">
                                <p className="mb-2">{getFileName(item.file)}</p>
                                <div className="flex align-items-center">
                                    <p className="m-0">
                                        {item.file instanceof File
                                            ? `${(item.file.size / 1024).toFixed(2)} KB`
                                            : "00.00 KB"}
                                    </p>
                                    <Badge className="ml-2" value="Default" />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <span
                        style={{
                            fontSize: "1em",
                            color: "var(--text-color-secondary)",
                        }}
                    >
            Upload File
          </span>
                )}
            </div>
        );
    };

    const pdfName = (event: any, id: any) => {
        const updatedDocuments = career?.documents?.map((item: any) => {
            if (Number(item.id) === Number(id)) {
                return {
                    ...item,
                    [event.target.name]: event.target.value,
                };
            }

            return item;
        });

        setCareer((prev: any) => ({
            ...prev,
            documents: updatedDocuments,
        }));
    };

    const addDoc = () => {
        const lastId =
            career.documents.length > 0 ? career?.documents?.at(-1)?.id : 0;

        setCareer((prev: any) => ({
            ...prev,
            documents: [
                ...prev.documents,
                {
                    id: Number(lastId) + 1,
                    name: "",
                    file: null,
                    disabled: false,
                },
            ],
        }));
    };

    const onUploadPdf = (event: any, id: any) => {
        const updatedDocuments = career?.documents?.map((item: any) => {
            if (Number(item.id) === Number(id)) {
                return {
                    ...item,
                    file: event.files[0],
                };
            }

            return item;
        });

        setCareer((prev: any) => ({
            ...prev,
            documents: updatedDocuments,
        }));
    };

    const onRemovePdf = (id: any) => {
        const updatedDocuments = career?.documents?.map((item: any) => {
            if (Number(item.id) === Number(id)) {
                return {
                    ...item,
                    file: "",
                };
            }

            return item;
        });

        setCareer((prev: any) => ({
            ...prev,
            documents: updatedDocuments,
        }));
    };

    const removeDocument = async (documentId: any, disabled: boolean) => {
        if (!disabled) {
            const docs = career?.documents?.filter(
                (car: any) => Number(car.id) !== Number(documentId)
            );

            setCareer((prev: any) => ({
                ...prev,
                documents: docs,
            }));

            return;
        }

        const removeLink = `${webInfo.baseUrl}/api/admin/careers-documents/${documentId}`;

        try {
            const res: any = await remove(removeLink);

            if (res.ok) {
                const remainFiles = career.documents.filter(
                    (item: any) => Number(item.id) !== Number(documentId)
                );

                setCareer((prev: any) => ({
                    ...prev,
                    documents: remainFiles,
                }));
            }
        } catch (error) {
            catchError(error);
        }
    };

    const updateProduct = async () => {
        const formData = new FormData();

        for (const key in career) {
            const value = career[key as keyof typeof career];

            if (key === "documents" || key === "expired_date") continue;

            if (value === null || value === undefined) continue;

            if (key === "image" || key === "meta_image") {
                if (value instanceof File) {
                    formData.append(key, value);
                }

                continue;
            }

            if (typeof value === "string") {
                if (value.trim() !== "") {
                    formData.append(key, value);
                }
            } else {
                formData.append(key, String(value));
            }
        }

        if (career.expired_date) {
            const formattedDate = moment(career.expired_date).format("YYYY-MM-DD");
            formData.append("expired_date", formattedDate);
        }

        if (career?.documents?.length > 0) {
            career.documents.forEach((item: any, index: number) => {
                if (item.file && item.file instanceof File) {
                    formData.append(`files[${index}][name]`, item.name || "");
                    formData.append(`files[${index}][file]`, item.file);
                }
            });
        }

        const editorValues = {
            description,
            job_requirement: jobRequirement,
            education_requirement: educationRequirement,
            how_to_apply: howToApply,
            job_benefits: jobBenefits,
        };

        Object.entries(editorValues).forEach(([key, value]) => {
            if (value && value.trim() !== "") {
                formData.append(key, value);
            }
        });

        const newLink = `${webInfo.baseUrl}/api/admin/careers/${id}`;

        try {
            const res = await post(newLink, false, formData);

            if (res.success) {
                callToast(toast, true, res.data.message);
            } else {
                showErrors(res.error.errors || res.error, toast);
            }
        } catch (error) {
            catchError(error);
        }
    };

    if (loading) {
        return (
            <Card>
                <div>
                    <div className="grid mt-3">
                        <div className="col-12 md:col-7">
                            <div className="p-fluid grid mb-3">
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                            </div>

                            <div className="p-fluid grid mb-3">
                                <div className="col-12 md:col-6">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                                <div className="col-12 md:col-6">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                            </div>

                            {[1, 2, 3, 4].map((item) => (
                                <div key={item} className="p-fluid grid mb-3">
                                    <div className="col-12 md:col-12">
                                        <Skeleton className="w-full" height="10rem" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="col-12 md:col-5">
                            <div className="p-fluid grid mb-3">
                                <div className="col-12 md:col-2">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                            </div>

                            <div className="p-fluid grid mb-3">
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                            </div>

                            <div className="p-fluid grid mb-3">
                                <div className="col-12 md:col-2">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                            </div>

                            <div className="p-fluid grid mb-5">
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                            </div>

                            <div className="p-fluid grid mt-5">
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="3rem" />
                                </div>
                                <div className="col-12 md:col-12">
                                    <Skeleton className="w-full" height="10rem" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        );
    }

    const header = (
        <div className="flex align-items-center justify-content-between p-4 pb-0">
            <h4 className="m-0">Career Details</h4>
        </div>
    );

    return (
        <Card header={header}>
            <Toast ref={toast} />

            <div className="grid">
                <div className="col-12 md:col-7">
                    <div className="pr-2">
                        <div className="p-fluid grid">
                            <div className="col-12 md:col-12">
                                <label className="font-medium">Title</label>
                                <InputText
                                    value={career.title}
                                    name="title"
                                    onChange={handleInput}
                                    placeholder="Enter career title"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label className="font-medium">Division *</label>
                                <Dropdown
                                    options={companyOptions}
                                    value={selectedCompany || null}
                                    onChange={(e: any) => {
                                        const selected = e.value;

                                        const foundCompany = depots?.find(
                                            (item: any) => Number(item?.id) === Number(selected?.code)
                                        );

                                        const deptOptions =
                                            foundCompany?.departments?.map((item: any) => ({
                                                name: item?.name,
                                                code: item?.id,
                                            })) || [];

                                        setDepartments(deptOptions);

                                        setCareer((prev: any) => ({
                                            ...prev,
                                            company_id: selected?.code || 0,
                                            company_department_id: 0,
                                        }));
                                    }}
                                    optionLabel="name"
                                    placeholder="Select Division"
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label className="font-medium">Department *</label>
                                <Dropdown
                                    options={conditionalDepartment}
                                    value={selectedDepartment || null}
                                    onChange={(e: any) => {
                                        setCareer((prev: any) => ({
                                            ...prev,
                                            company_department_id: e.value?.code || 0,
                                        }));
                                    }}
                                    optionLabel="name"
                                    placeholder="Select Department"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label className="font-medium">Salary From</label>
                                <InputText
                                    type="number"
                                    name="salary_from"
                                    onChange={handleInput}
                                    value={career.salary_from}
                                    placeholder="BDT 00.00"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label className="font-medium">Salary To</label>
                                <InputText
                                    type="number"
                                    value={career.salary_to}
                                    name="salary_to"
                                    onChange={handleInput}
                                    placeholder="BDT 00.00"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label className="font-medium">Job Type</label>
                                <InputText
                                    value={career.job_type}
                                    name="job_type"
                                    onChange={handleInput}
                                    placeholder="Job Type"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label className="font-medium">Vacancy</label>
                                <InputText
                                    value={career.number_of_vacancy}
                                    name="number_of_vacancy"
                                    onChange={handleInput}
                                    placeholder="Enter vacancy"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label className="font-medium">Location</label>
                                <InputText
                                    value={career.address}
                                    name="address"
                                    onChange={handleInput}
                                    placeholder="Location"
                                />
                            </div>

                            <div className="col-12 md:col-4">
                                <label className="font-medium">Expired Date</label>
                                <Calendar
                                    value={career?.expired_date || null}
                                    onChange={(e: any) => {
                                        setCareer((prev: any) => ({
                                            ...prev,
                                            expired_date: e.value,
                                        }));
                                    }}
                                    placeholder="Expired date"
                                />
                            </div>

                            <div className="col-12 md:col-12">
                                <label className="font-medium">Short Description</label>
                                <InputTextarea
                                    style={{ height: "150px" }}
                                    value={career.short_description}
                                    name="short_description"
                                    onChange={handleInput}
                                    placeholder="Enter short description"
                                />
                            </div>

                            <div className="col-12 md:col-12">
                                <label className="font-medium">Description</label>
                                <TextEditor
                                    value={description || ""}
                                    setValue={(content: string) => setDescription(content)}
                                />
                            </div>

                            <div className="col-12">
                                <label className="font-medium">Requirement</label>
                                <TextEditor
                                    value={jobRequirement || ""}
                                    setValue={(content: string) => setJobRequirement(content)}
                                />
                            </div>

                            <div className="col-12">
                                <label className="font-medium">Education Requirement</label>
                                <TextEditor
                                    value={educationRequirement || ""}
                                    setValue={(content: string) =>
                                        setEducationRequirement(content)
                                    }
                                />
                            </div>

                            <div className="col-12">
                                <label className="font-medium">Job Benefits</label>
                                <TextEditor
                                    value={jobBenefits || ""}
                                    setValue={(content: string) => setJobBenefits(content)}
                                />
                            </div>

                            <div className="col-12">
                                <label className="font-medium">How to Apply</label>
                                <TextEditor
                                    value={howToApply || ""}
                                    setValue={(content: string) => setHowToApply(content)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 md:col-5">
                    <div className="pl-2">
                        <div className="col-12">
                            <label className="font-medium">Thumbnail Image</label>
                            <FileUpload
                                name="image"
                                url="/api/upload"
                                multiple={false}
                                accept="image/*"
                                maxFileSize={1024 * 1024}
                                emptyTemplate={defaultImagePreviewer}
                                onSelect={onUpload}
                                onRemove={onRemove}
                            />
                        </div>

                        <div className="col-12">
                            <div className="flex justify-content-between mt-4 mb-2">
                                <h4 className="mb-0">Documents</h4>
                                <Button
                                    size="small"
                                    onClick={addDoc}
                                    icon="pi pi-plus"
                                    label="Add New"
                                    outlined
                                />
                            </div>

                            {career?.documents?.map((item: any) => {
                                return (
                                    <Card key={item.id} className="mb-2">
                                        <div className="mb-2">
                                            <label className="font-medium">File Name</label>
                                            <InputText
                                                disabled={item.disabled}
                                                onChange={(e) => pdfName(e, item.id)}
                                                className="w-full mb-2"
                                                placeholder="File Name"
                                                value={item.name || ""}
                                                name="name"
                                            />
                                        </div>

                                        <div>
                                            <label className="font-medium">File</label>
                                            <FileUpload
                                                name={`file[${item.id}]`}
                                                url="/api/upload"
                                                multiple={false}
                                                accept="application/pdf"
                                                disabled={item?.disabled}
                                                maxFileSize={10 * 1024 * 1024}
                                                emptyTemplate={() => defaultPdfPreviewer(item)}
                                                onSelect={(e: any) => onUploadPdf(e, item.id)}
                                                onRemove={() => onRemovePdf(item.id)}
                                            />
                                        </div>

                                        <Button
                                            onClick={() => removeDocument(item.id, item.disabled)}
                                            outlined
                                            label="Remove"
                                            size="small"
                                            severity="danger"
                                            className="mt-2"
                                            icon="pi pi-minus"
                                        />
                                    </Card>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid p-4">
                        <div className="col-12">
                            <h4 className="mb-3">SEO Information</h4>
                        </div>

                        <div className="col-12">
                            <label className="font-medium">Meta Title</label>
                            <InputText
                                name="meta_title"
                                value={career.meta_title || ""}
                                onChange={handleChange}
                                placeholder="Meta Title"
                                className="w-full"
                            />
                        </div>

                        <div className="col-12 md:col-6">
                            <label className="font-medium">Meta Author</label>
                            <InputText
                                name="meta_author"
                                value={career.meta_author || ""}
                                onChange={handleChange}
                                placeholder="Meta Author"
                                className="w-full"
                            />
                        </div>

                        <div className="col-12 md:col-6">
                            <label className="font-medium">Meta Robots</label>
                            <Dropdown
                                value={career.meta_robots || ""}
                                options={[
                                    { label: "Index, Follow", value: "index, follow" },
                                    { label: "No Index, Follow", value: "noindex, follow" },
                                    { label: "Index, No Follow", value: "index, nofollow" },
                                    { label: "No Index, No Follow", value: "noindex, nofollow" },
                                ]}
                                onChange={(e: any) =>
                                    setCareer((prev: any) => ({
                                        ...prev,
                                        meta_robots: e.value,
                                    }))
                                }
                                placeholder="Select Robots Option"
                                className="w-full"
                            />
                        </div>

                        <div className="col-12">
                            <label className="font-medium">Meta Description</label>
                            <InputTextarea
                                name="meta_description"
                                value={career.meta_description || ""}
                                onChange={handleChange}
                                rows={4}
                                placeholder="Meta Description"
                                className="w-full"
                                autoResize
                            />
                        </div>

                        <div className="col-12">
                            <label className="font-medium">Meta Keywords</label>
                            <InputText
                                name="meta_keywords"
                                value={career.meta_keywords || ""}
                                onChange={handleChange}
                                placeholder="keyword1, keyword2, keyword3"
                                className="w-full"
                            />
                        </div>

                        <div className="col-12">
                            <label className="font-medium">Meta Image (1024 kB max)</label>
                            <FileUpload
                                name="meta_image"
                                url="/api/upload"
                                multiple={false}
                                accept="image/*"
                                maxFileSize={1024 * 1024}
                                emptyTemplate={defaultMetaImagePreviewer}
                                onSelect={onMetaUpload}
                                onRemove={onMetaRemove}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12 flex justify-content-end gap-2 mt-3">
                    <Button
                        label="Back"
                        icon="pi pi-chevron-left"
                        className="p-button-secondary"
                    />
                    <Button
                        loading={postLoading}
                        onClick={updateProduct}
                        label="Update Career"
                        icon="pi pi-check"
                    />
                </div>
            </div>
        </Card>
    );
};

export default CareerDetails;